/**
 * /start/confirmation — funnel step 5 (App Access).
 *
 * Two states:
 *  - pending_payment: the user just returned from Stripe-hosted Checkout and
 *    the webhook may not have landed yet. We call GET /funnel/checkout/verify
 *    with the success URL's ?session_id (synchronous finalization, idempotent
 *    with the webhook) and poll funnel state briefly; if payment still hasn't
 *    confirmed, we show a "still processing" fallback instead of a false
 *    success.
 *  - paid: the post-payment copy (manual approval reinstated — ClickUp
 *    86baab6dk: payment puts the account in 'Pending approval'; the team
 *    prepares it, assigns the story persona, and emails when app access is
 *    ready), the latest order summary from GET /funnel/state incl. any promo
 *    discount, an account link, and a support link. The sessionStorage
 *    selection is cleared HERE on the Stripe path (PaymentPage keeps it so a
 *    cancel-return can retry).
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Clock, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FunnelLayout } from '@/components/funnel/FunnelLayout';
import { useFunnelState, FUNNEL_STATE_QUERY_KEY } from '@/hooks/useFunnel';
import { apiClient } from '@/services/api-client';
import { SUPPORT_URL, clearFunnelSelection, clearShippingAddress, formatPrice } from '@/lib/funnel';

/** Poll funnel state every 2s while pending; give up after 10 tries (~20s). */
const PENDING_POLL_INTERVAL_MS = 2000;
const PENDING_POLL_MAX_ATTEMPTS = 10;

export function ConfirmationPage() {
  const { data: state, isLoading } = useFunnelState();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const orderStatus = state?.latestOrder?.status ?? null;
  const isPendingPayment = orderStatus === 'pending_payment';
  const [pollTimedOut, setPollTimedOut] = useState(false);

  // Returning from Stripe: verify the session server-side once — this
  // finalizes the order immediately when the webhook hasn't landed yet
  // (idempotent with the webhook; the server authorizes by order ownership).
  useEffect(() => {
    if (!sessionId) return;
    void apiClient
      .get(`/api/v1/funnel/checkout/verify?session_id=${encodeURIComponent(sessionId)}`)
      .then(() => queryClient.invalidateQueries({ queryKey: FUNNEL_STATE_QUERY_KEY }));
  }, [sessionId, queryClient]);

  // While the order is pending, poll briefly for the webhook to land.
  useEffect(() => {
    if (!isPendingPayment) return;
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      if (attempts > PENDING_POLL_MAX_ATTEMPTS) {
        clearInterval(timer);
        setPollTimedOut(true);
        return;
      }
      void queryClient.invalidateQueries({ queryKey: FUNNEL_STATE_QUERY_KEY });
    }, PENDING_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isPendingPayment, queryClient]);

  // The purchase is settled — the sessionStorage selection has served its
  // purpose (the Stripe path deliberately does not clear it on PaymentPage).
  useEffect(() => {
    if (orderStatus === 'paid') {
      clearFunnelSelection();
      clearShippingAddress();
    }
  }, [orderStatus]);

  if (!isLoading && isPendingPayment) {
    return (
      <FunnelLayout step={6} width="2xl">
        <div className="text-center">
          {pollTimedOut ? (
            <>
              <Clock className="mx-auto mb-4 h-14 w-14 text-muted-foreground" />
              <h1 className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                Your payment is still processing
              </h1>
              <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
                This can occasionally take a little longer. You'll receive an email confirmation as
                soon as it completes — you can safely close this page.
              </p>
              <div className="mt-6 flex flex-col items-center gap-3">
                <Button asChild variant="outline" size="lg">
                  <Link to="/start/payment">Return to payment</Link>
                </Button>
                <a
                  href={SUPPORT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground underline-offset-4 hover:underline"
                >
                  Contact our support team
                </a>
              </div>
            </>
          ) : (
            <>
              <Loader2 className="mx-auto mb-4 h-14 w-14 animate-spin text-primary" />
              <h1 className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                Finalizing your payment…
              </h1>
              <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
                Hang tight — we're confirming your payment with Stripe. This usually takes just a
                few seconds.
              </p>
            </>
          )}
        </div>
      </FunnelLayout>
    );
  }

  const discountCents = state?.latestOrder?.discountCents ?? 0;

  return (
    <FunnelLayout step={6} width="2xl">
      <div className="text-center">
        <CheckCircle2 className="mx-auto mb-4 h-14 w-14 text-primary" />
        <h1 className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          Thank you — your order is confirmed
        </h1>
        {/* Exact post-payment copy from ClickUp 86baab6dk — do not reword
            without Ziva. */}
        <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
          Your payment was received. Our team will prepare your account, assign your story persona,
          and notify you when your app access is ready.
        </p>
      </div>

      <Card className="mt-8">
        <CardContent className="pt-6">
          <h2 className="mb-4 text-base font-semibold text-foreground">Your order</h2>
          {isLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          ) : state?.latestOrder ? (
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Order reference</span>
                <span className="font-mono text-xs text-foreground">
                  {state.latestOrder.id.slice(0, 8).toUpperCase()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Date</span>
                <span className="text-foreground">
                  {format(new Date(state.latestOrder.createdAt), 'MMMM d, yyyy')}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Status</span>
                <span className="capitalize text-foreground">
                  {state.latestOrder.status.replace('_', ' ')}
                </span>
              </div>
              {discountCents > 0 && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="text-foreground">
                      {formatPrice(state.latestOrder.totalCents)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Promo discount</span>
                    <span className="text-foreground">−{formatPrice(discountCents)}</span>
                  </div>
                </>
              )}
              <div className="flex items-center justify-between border-t border-border pt-3 text-base font-semibold text-foreground">
                <span>Total paid</span>
                <span>
                  {formatPrice(Math.max(0, state.latestOrder.totalCents - discountCents))}
                </span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No order found on your account.</p>
          )}
        </CardContent>
      </Card>

      {/* Next steps — don't dead-end the user after payment (review F3).
          App access is unlocked by the team after account preparation
          (ClickUp 86baab6dk), so the primary CTA is the account page where
          the "Pending approval" status lives. */}
      <div className="mt-6 flex flex-col items-center gap-3">
        <Button asChild size="lg">
          <Link to="/account">Go to your account</Link>
        </Button>
      </div>

      <Card className="mt-6">
        <CardContent className="flex items-start gap-3 pt-6">
          <Clock className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
          <div>
            <h2 className="text-base font-semibold text-foreground">What happens next</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Your account status is now <span className="text-foreground">Pending approval</span>.
              Our team is preparing your account and matching you with the story persona best suited
              to guide your interviews. No action is needed from you — we&apos;ll email you the
              moment your app access is ready.
            </p>
          </div>
        </CardContent>
      </Card>

      <p className="mt-8 text-center text-sm text-muted-foreground">
        Questions about your order?{' '}
        <a
          href={SUPPORT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-foreground underline-offset-4 hover:underline"
        >
          Contact our support team
        </a>
      </p>
    </FunnelLayout>
  );
}
